import React from 'react';
import { format, parseISO } from 'date-fns';
import { Card, CardContent, CardTitle } from '@/ui/components/card';
import { Progress } from '@/ui/components/progress';
import { delimited } from '../../../common/utils/commonUtil';
import {
  ANGEL_TYPE,
  STATUS,
  type Project,
} from '../../models/projects/project';
import {
  achievementRate,
  isPreOpen,
} from '../../models/projects/projectProgress';

interface Props {
  project: Project;
}

// 残り時間を数値と単位に分ける。終了時刻を過ぎたら null。閾値は app/helpers/projects_helper.rb の
// project_remain_time と同じ（cardStatus.tsx の projectRemainTime は「2日後」の文字列を返すため、「残り 2日」の表示には使えない）
function remainingTime(
  finishAt: string,
): { value: number; unit: string } | null {
  const seconds = (parseISO(finishAt).getTime() - Date.now()) / 1000;

  if (seconds > 86400)
    return { value: Math.floor(seconds / 86400), unit: '日' };
  if (seconds > 3600)
    return { value: Math.floor(seconds / 3600), unit: '時間' };
  if (seconds > 0) return { value: Math.floor(seconds / 60), unit: '分' };
  return null;
}

// 募集中以降の状態（事前開示中は呼び出し側で出す）。判定の順序は cardStatus.tsx と同じ。
// 不成立・取り下げの分岐は持たない（API を order=in_progress で呼ぶので募集中とキャンセル待ちしか返らない）。
// 募集中のまま終了時刻を過ぎた案件（状態を切り替えるジョブが動く前）は、募集終了と同じ表示にする
function StatusBox({ project }: Props) {
  const remaining =
    project.status === STATUS.inProgress
      ? remainingTime(project.finishAt)
      : null;
  if (remaining != null) {
    return (
      <span className="tw:flex tw:items-baseline tw:gap-2 tw:font-bold">
        <span className="tw:text-xs tw:leading-none">残り</span>
        <span className="tw:flex tw:items-baseline tw:gap-0.5">
          <span className="tw:text-lg tw:leading-none">{remaining.value}</span>
          <span className="tw:text-sm tw:leading-none">{remaining.unit}</span>
        </span>
      </span>
    );
  }
  if (project.status <= STATUS.succeeded && project.waitingWaitOrder) {
    return (
      <>
        <span className="tw:text-xs tw:leading-none tw:font-bold">
          キャンセル待ち
        </span>
        <span className="tw:text-xs tw:leading-none tw:font-bold">
          残り {project.waitRemainTime}
        </span>
      </>
    );
  }
  return (
    <span className="tw:text-xs tw:leading-none tw:font-bold">
      募集が終了しています
    </span>
  );
}

// シミュレーター結果の下に並べる案件カード。projects/card.tsx は Bootstrap の 2 列グリッドとレガシー SCSS に
// 結び付いているため DS の Card で組み直す。表示の判定は cardStatus / cardPercentage と揃える
export default function AngelTaxProjectCard({ project }: Props) {
  const preOpen = isPreOpen(project);
  const rate = achievementRate(project);

  return (
    <a
      href={`/projects/${project.id}`}
      className="tw:block tw:h-full tw:text-inherit tw:no-underline tw:transition-opacity tw:hover:text-inherit tw:hover:opacity-80"
    >
      {/* 画像直下に会社ロゴを 30px 重ねるため、Card の縦の余白は 0 にして本文側で持つ */}
      <Card className="tw:h-full tw:font-noto tw:[--card-spacing:0px] tw:[--card-spacing-inline:--spacing(5)]">
        <img
          src={project.image.thumb16By9.url}
          alt=""
          // 高さは固定せず 16:9 で伸縮させる（1 列で幅いっぱいになっても切り抜かない）
          className="tw:aspect-video tw:w-full tw:object-cover"
        />
        {/* 本文の高さは Figma のカード 448px − 画像 172px を下限にして、事前開示中でも他のカードと揃える */}
        <CardContent className="tw:relative tw:flex tw:min-h-69 tw:flex-1 tw:flex-col tw:gap-3 tw:pt-11 tw:pb-5">
          <div className="tw:absolute tw:-top-7.5 tw:left-5 tw:flex tw:items-end tw:gap-3">
            <img
              src={project.company.imageThumbUrl}
              alt=""
              width="60"
              height="60"
              className="tw:size-15 tw:border tw:border-background tw:bg-background tw:object-cover"
            />
            <span className="tw:text-sm tw:leading-normal tw:font-medium">
              {project.originalCompanyName || project.company.name}
            </span>
          </div>
          <CardTitle as="h3" className="tw:line-clamp-2">
            {project.name}
          </CardTitle>
          <div className="tw:flex tw:min-h-6">
            {project.angelType !== ANGEL_TYPE.none && (
              <span className="tw:inline-flex tw:rounded-full tw:border tw:border-brand tw:px-2.5 tw:py-1.5 tw:font-hiragino tw:text-xs tw:leading-none tw:font-semibold tw:tracking-text tw:text-brand">
                エンジェル税制
              </span>
            )}
          </div>
          {/* 事前開示中は金額・進捗の代わりに、開示中の表示が残りの高さいっぱいに広がる */}
          {preOpen ? (
            <div className="tw:flex tw:flex-1 tw:flex-col tw:items-center tw:justify-center tw:gap-1 tw:bg-input-empty-background tw:px-2 tw:py-2 tw:text-center">
              <span className="tw:text-lg tw:leading-normal tw:font-bold">
                案件事前開示中
              </span>
              <span className="tw:text-xs tw:leading-normal tw:font-medium tw:tracking-text">
                {format(parseISO(project.startAt), 'yyyy年M月d日H:mm')} 申込開始
              </span>
            </div>
          ) : (
            <>
              <div className="tw:flex tw:flex-1 tw:flex-col tw:justify-end tw:gap-0.5">
                <p className="tw:m-0 tw:flex tw:items-baseline tw:gap-0.5 tw:leading-normal">
                  <span className="tw:text-lg tw:font-bold">
                    {delimited(project.solicited)}円
                  </span>
                  <span className="tw:flex tw:items-baseline tw:gap-0.5 tw:font-medium tw:text-muted-foreground">
                    <span className="tw:text-sm">/</span>
                    <span className="tw:text-xs">
                      {delimited(project.solicit)}円
                    </span>
                  </span>
                </p>
                <Progress value={Math.min(rate, 100)} />
                <p className="tw:m-0 tw:flex tw:items-baseline tw:justify-end tw:gap-1 tw:text-muted-foreground">
                  <span className="tw:text-xs tw:tracking-text">
                    目標達成率
                  </span>
                  <span className="tw:text-sm tw:font-bold">{rate}%</span>
                </p>
              </div>
              <div className="tw:flex tw:min-h-13 tw:flex-col tw:items-center tw:justify-center tw:gap-1 tw:bg-input-empty-background tw:py-2">
                <StatusBox project={project} />
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </a>
  );
}
